import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Modal, Button, Row, Alert, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './sessionadmin.css';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions, adminActions } from '../../redux/_actions';
import TableComponent from '../helpers/tablecomponent';
import { dateTime } from '../../utilities/conversions';
import SessionDetail from './sessiondetail';
import { accessType, sessionStatus, uiState } from '../../redux/_constants';
import { modeType } from '../../utilities/constants';
import { useHistory } from 'react-router-dom';
import { Loading, ScrollLoading } from '../helpers/loading';
import { SearchInput, addBold, noResults } from '../helpers/search';

function SessionAdmin(props) {
  const { setIsForm } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const { LIST, DETAIL, EDIT, DELETE } = modeType;
  const [mode, setMode] = useState(LIST);
  const [sessionId, setSessionId] = useState(null);
  const [sessionKey, setSessionKey] = useState(null);
  const alert = useSelector((state) => state.alert);
  const sessions = useSelector((state) => state.admin.sessions);
  const nextToken = useSelector(
    (state) => state.admin.sessions.tokens.nextToken
  );
  const searchQuery = useSelector(
    (state) => state.admin.sessions.tokens.searchQuery
  );
  const searchToken = useSelector(
    (state) => state.admin.sessions.tokens.searchToken
  );

  const [searchTerm, setSearchTerm] = useState(searchQuery || '');
  const [tempSearchTerm, setTempSearchTerm] = useState(searchQuery || ''); // used for async holding of query
  const [filters, setFilters] = useState({
    gender: null,
    status: null,
    start: null,
    demo: null,
    startDate: null,
    endDate: null,
  });
  // Server-side date range filter: 'near' (±7 days), '+30', '+90', 'all'
  const [dateRangeFilter, setDateRangeFilter] = useState('near');
  const isFirstRender = useRef(true);

  // Helper to compute date range for server query
  const getDateRangeForFilter = (filterValue) => {
    const now = new Date();
    switch (filterValue) {
      case 'near': {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        const end = new Date(now);
        end.setDate(end.getDate() + 7);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
      }
      case '+30': {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        const end = new Date(now);
        end.setDate(end.getDate() + 30);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
      }
      case '+90': {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        const end = new Date(now);
        end.setDate(end.getDate() + 90);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
      }
      case 'all':
      default:
        return null; // No date filter
    }
  };

  const dispatch = useDispatch();
  const history = useHistory();
  const numSessions = sessions?.allIds?.length || 0;
  const isSearch = searchTerm !== '';

  useEffect(() => {
    if (!alert.clear) {
      setIsLoading(false);
    }
  }, [alert.clear]);

  // Single effect handles both initial load and date range changes
  useEffect(() => {
    if (isFirstRender.current) {
      dispatch(alertActions.clear());
      isFirstRender.current = false;
    }
    setIsLoading(true);
    const dateRange = getDateRangeForFilter(dateRangeFilter);
    dispatch(adminActions.getSessions({ dateRange, nextToken: null }));
  }, [dateRangeFilter, dispatch]);

  // Only stop loading when we actually have sessions data
  useEffect(() => {
    if (sessions?.allIds?.length > 0) {
      setIsLoading(false);
      setIsScrollLoading(false);
    }
    setSearchTerm(tempSearchTerm);
  }, [sessions, tempSearchTerm]);

  const sessionDetail = (e, row) => {
    if (!alert.clear) {
      dispatch(alertActions.clear());
    }
    setSessionId(row.id);
    setSessionKey(row.key);
    setMode(DETAIL);
    setIsForm(true);
  };

  const back = () => {
    if (!alert.clear) {
      dispatch(alertActions.clear());
    }
    setMode(LIST);
    setIsForm(false);
  };

  const headers = useMemo(
    () => [
      { Header: '#', accessor: 'order' },
      {
        Header: 'Name',
        accessor: 'name',
        maxWidth: 50,
        Cell: (r) => addBold(r.cell.value, searchTerm),
      },
      { Header: 'Type', accessor: 'type' },
      { Header: 'Gender', accessor: 'gender' },
      {
        Header: 'Key',
        accessor: 'key',
        Cell: ({ row }) => (
          <Link
            to={{
              pathname: '/session',
              search: `?s=${row.values.key}`,
              state: { fromAdmin: true },
            }}
          >
            {row.values.key}
          </Link>
        ),
      },
      {
        Header: 'Created',
        accessor: 'created',
        Cell: ({ row }) => {
          return dateTime(row.values.created);
        },
      },
      {
        Header: 'Start',
        accessor: 'start',
        Cell: ({ row }) => {
          return dateTime(row.values.start);
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: (r) => {
          const { row, column, cell, updateMyData } = r;

          return (
            <Form.Control
              as="select"
              label="Status"
              id={`status-${row.original.id}`}
              value={cell.value}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                const payload = {
                  ...sessions.byId[row.original.id],
                  status: e.target.value,
                };
                dispatch(adminActions.editSession(payload));
                updateMyData(
                  row.index,
                  column.id,
                  e.target.value === '-' ? '' : e.target.value
                );
              }}
            >
              {Object.keys(sessionStatus).map((statusType) => (
                <option key={statusType} value={statusType}>
                  {statusType}
                </option>
              ))}
            </Form.Control>
          );
        },
      },
      {
        Header: 'Access',
        accessor: 'access',
        Cell: (r) => {
          const { row, column, cell, updateMyData } = r;
          return (
            <Form.Control
              as="select"
              label="Access"
              id={`access-${row.original.id}`}
              value={cell.value}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                const payload = {
                  ...sessions.byId[row.original.id],
                  access: e.target.value,
                };
                dispatch(adminActions.editSession(payload));
                updateMyData(
                  row.index,
                  column.id,
                  e.target.value === '-' ? '' : e.target.value
                );
              }}
            >
              {Object.keys(accessType).map((accessValue) => {
                return (
                  <option key={accessValue} value={accessValue}>
                    {accessValue}
                  </option>
                );
              })}
            </Form.Control>
          );
        },
      },
    ],
    [searchTerm, sessions]
  );

  const data = useMemo(() => {
    let filteredData = sessions.allIds.map((id, i) => {
      const {
        name,
        type,
        gender,
        sessionKey,
        createdAt,
        startAt,
        access,
        status /*athletes, league*/,
      } = sessions.byId[id];

      const row = {
        order: i + 1,
        name: name,
        type: type,
        gender: gender,
        key: sessionKey,
        created: createdAt,
        start: startAt,
        status: status,
        access: access,
        id: id,
      };
      return row;
    });

    // Apply filters
    if (filters.gender) {
      console.log(
        'Filtering by gender:',
        filters.gender,
        typeof filters.gender
      );
      console.log('Before filter:', filteredData.length);
      filteredData = filteredData.filter((row) => {
        console.log('Row gender:', row.gender, typeof row.gender);
        console.log(
          'Comparison:',
          row.gender === filters.gender,
          JSON.stringify(row.gender),
          JSON.stringify(filters.gender)
        );
        return row.gender === filters.gender;
      });
      console.log('After filter:', filteredData.length);
    }
    if (filters.status) {
      filteredData = filteredData.filter(
        (row) => row.status === filters.status
      );
    }
    if (filters.start === 'upcoming') {
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      filteredData = filteredData.filter((row) => {
        const startDate = new Date(row.start);
        return startDate >= today && startDate <= nextWeek;
      });
    }
    if (filters.demo === 'hide') {
      filteredData = filteredData.filter((row) => {
        return !row.name.toLowerCase().includes('demo');
      });
    }
    if (filters.startDate) {
      filteredData = filteredData.filter((row) => {
        const sessionDate = new Date(row.start);
        const startDate = new Date(filters.startDate);
        startDate.setHours(0, 0, 0, 0);

        if (filters.endDate) {
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999);
          return sessionDate >= startDate && sessionDate <= endDate;
        }

        return sessionDate.toDateString() === startDate.toDateString();
      });
    }

    return filteredData;
  }, [sessions, filters]);

  const table = () => {
    const initialState = {
      sortBy: [{ id: 'start', desc: true }],
    };

    const empty = numSessions === 0 || data.length === 0;

    return empty ? (
      noResults('sessions')
    ) : (
      <TableComponent
        columns={headers}
        data={data}
        onClick={sessionDetail}
        initialState={initialState}
        searchTerm={searchTerm}
        textSort={true}
        showFooter={true}
        filterColumns={['name']}
      />
    );
  };

  const handleScroll = (e) => {
    const { scrollHeight, scrollTop, scrollLeft, clientHeight } = e.target;
    const bottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 1;
    const nextPage =
      (isSearch && searchToken !== null) ||
      (!isSearch && nextToken !== null && numSessions > 0);
    const vertScroll = scrollLeft === 0;

    if (bottom && nextPage && vertScroll && !isScrollLoading) {
      setIsScrollLoading(true);
      const dateRange = getDateRangeForFilter(dateRangeFilter);
      dispatch(
        adminActions.getSessions({
          searchQuery: searchTerm,
          dateRange,
          nextToken: isSearch ? searchToken : nextToken,
        })
      );
    }
  };

  const handleSearch = (query) => {
    setTempSearchTerm(query);

    if (query && query !== '') {
      const dateRange = getDateRangeForFilter(dateRangeFilter);
      dispatch(
        adminActions.getSessions({
          searchQuery: query,
          dateRange,
          nextToken: null,
        })
      );
    } else {
      // clear search - re-fetch with current date range
      setSearchTerm(query);
      const dateRange = getDateRangeForFilter(dateRangeFilter);
      dispatch(adminActions.getSessions({ dateRange, nextToken: null }));
    }
  };

  const adminBody = () => {
    return (
      <>
        {!alert.clear ? (
          <Row style={{ padding: '0 15px', margin: '1rem -15px' }}>
            <Alert
              dismissible
              onClose={() => dispatch(alertActions.clear())}
              variant={alert.type === 'alert-danger' ? 'danger' : 'success'}
              style={{ textAlign: 'center', width: '100%' }}
            >
              {alert.message}
            </Alert>
          </Row>
        ) : null}
        {isLoading ? <Loading /> : table()}
      </>
    );
  };

  const footer = () => {
    return (
      <>
        <Row className="vCenter" style={{ margin: '0 auto' }}>
          <Button
            variant="outline-primary"
            className="formButton"
            onClick={() => history.push(uiState.CREATE)}
            disabled={isLoading}
          >
            Add session
          </Button>
        </Row>
        <Row />
      </>
    );
  };

  const FilterPill = ({ label, value, filterKey, className = '' }) => (
    <Button
      variant={filters[filterKey] === value ? 'secondary' : 'outline-secondary'}
      className={`rounded-pill py-0 px-2 mx-0 ${className}`}
      size="sm"
      onClick={() =>
        setFilters((prev) => ({
          ...prev,
          [filterKey]: prev[filterKey] === value ? null : value,
        }))
      }
    >
      {label}
    </Button>
  );

  const headerMsg = () => {
    return (
      <>
        <span style={{ whiteSpace: 'nowrap', paddingRight: '2rem' }}>
          Session Admin
        </span>
        <div className="d-flex align-items-center" style={{ gap: '4px' }}>
          <Button
            variant={
              dateRangeFilter === 'near' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2"
            size="sm"
            onClick={() => setDateRangeFilter('near')}
          >
            ±7 Days
          </Button>
          <Button
            variant={
              dateRangeFilter === '+30' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2"
            size="sm"
            onClick={() => setDateRangeFilter('+30')}
          >
            +30 Days
          </Button>
          <Button
            variant={
              dateRangeFilter === '+90' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2"
            size="sm"
            onClick={() => setDateRangeFilter('+90')}
          >
            +90 Days
          </Button>
          <Button
            variant={
              dateRangeFilter === 'all' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2"
            size="sm"
            onClick={() => setDateRangeFilter('all')}
          >
            All
          </Button>
          <span className="text-muted" style={{ padding: '0 2px' }}>
            |
          </span>
          <FilterPill label="Male" value="MALE" filterKey="gender" />
          <FilterPill label="Female" value="FEMALE" filterKey="gender" />
          <FilterPill label="LIVE" value="LIVE" filterKey="status" />
          <FilterPill label="Hide Demos" value="hide" filterKey="demo" />
          <DatePicker
            selectsRange={true}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={(update) => {
              const [start, end] = update;
              setFilters((prev) => ({
                ...prev,
                startDate: start,
                endDate: end,
              }));
            }}
            dateFormat="MM/dd/yyyy"
            className="date-picker-pill"
            placeholderText="Date Range"
            isClearable
            customInput={
              <Button
                size="sm"
                variant={filters.startDate ? 'secondary' : 'outline-secondary'}
                className="rounded-pill py-0 px-3"
              >
                {filters.startDate
                  ? filters.endDate
                    ? `${new Date(
                        filters.startDate
                      ).toLocaleDateString()} - ${new Date(
                        filters.endDate
                      ).toLocaleDateString()}`
                    : new Date(filters.startDate).toLocaleDateString()
                  : 'Date Range'}
              </Button>
            }
          />
        </div>
      </>
    );
  };

  const listModal = () => {
    return (
      <>
        <Modal.Header className="searchHeader">
          {headerMsg()}
          {numSessions !== 0 ? (
            <SearchInput searchTerm={searchTerm} search={handleSearch} />
          ) : null}
        </Modal.Header>
        <Modal.Body className="setupForm" onScroll={handleScroll}>
          {adminBody()}
          {isScrollLoading ? <ScrollLoading /> : null}
        </Modal.Body>
        <Modal.Footer className="setupFooter">{footer()}</Modal.Footer>
      </>
    );
  };

  const modal = () => {
    switch (mode) {
      case EDIT:
        break;
      case DELETE:
        break;
      case LIST:
        return listModal();
      case DETAIL:
        return (
          <SessionDetail
            back={back}
            sessionId={sessionId}
            sessionKey={sessionKey}
          />
        );
      default:
        return null;
    }
  };

  return modal();
}

export default SessionAdmin;
